/**
 * Utilitários de máscaras para inputs do formulário
 */

// Remove all non-numeric characters
export function onlyNumbers(value: string): string {
  return value.replace(/\D/g, '');
}

// CPF Mask: 000.000.000-00
export function maskCPF(value: string): string {
  const numbers = onlyNumbers(value);
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
}

// CNPJ Mask: 00.000.000/0000-00
export function maskCNPJ(value: string): string {
  const numbers = onlyNumbers(value);
  return numbers
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
}

// CPF or CNPJ Mask (auto-detect)
export function maskCPFOrCNPJ(value: string): string {
  const numbers = onlyNumbers(value);
  if (numbers.length <= 11) {
    return maskCPF(value);
  }
  return maskCNPJ(value);
}

// Phone Mask: (00) 00000-0000 or (00) 0000-0000
export function maskPhone(value: string): string {
  const numbers = onlyNumbers(value);
  if (numbers.length <= 10) {
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  }
  return numbers
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
}

// CEP Mask: 00000-000
export function maskCEP(value: string): string {
  const numbers = onlyNumbers(value);
  return numbers.replace(/(\d{5})(\d{1,3})/, '$1-$2').replace(/(-\d{3})\d+?$/, '$1');
}

// CNAE Mask: 0000-0/00
export function maskCNAE(value: string): string {
  const numbers = onlyNumbers(value);
  return numbers
    .replace(/(\d{4})(\d)/, '$1-$2')
    .replace(/(-\d)(\d{2})/, '$1/$2')
    .replace(/(\/\d{2})\d+?$/, '$1');
}

// Date Mask: DD/MM/YYYY
export function maskDate(value: string): string {
  const numbers = onlyNumbers(value);
  return numbers
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\/\d{4})\d+?$/, '$1');
}

// Currency Mask (Brazilian format): R$ 0.000,00
export function maskCurrency(value: string | number, currency: 'BRL' | 'USD' = 'BRL'): string {
  let numValue: number;

  if (typeof value === 'string') {
    // Remove currency symbols and convert to number
    const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    numValue = parseFloat(cleaned) || 0;
  } else {
    numValue = value;
  }

  const formatter = new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(numValue);
}

// Number with thousand separator (Brazilian format): 0.000,00
export function maskNumber(value: string | number, decimalPlaces: number = 2): string {
  let numValue: number;

  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    numValue = parseFloat(cleaned) || 0;
  } else {
    numValue = value;
  }

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(numValue);
}

// Percentage Mask: 00,00%
export function maskPercentage(value: string | number): string {
  let numValue: number;

  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    numValue = parseFloat(cleaned) || 0;
  } else {
    numValue = value;
  }

  return `${maskNumber(numValue, 2)}%`;
}

// Area Mask (Hectares): 0.000,00 Ha
export function maskArea(value: string | number): string {
  return `${maskNumber(value, 2)} Ha`;
}

// Volume Mask (Tons): 0.000,00 Tons
export function maskVolume(value: string | number): string {
  return `${maskNumber(value, 2)} Tons`;
}

// Sacas Mask: 0.000,00 Sacas
export function maskSacas(value: string | number): string {
  return `${maskNumber(value, 2)} Sacas`;
}

// Unmask functions - remove formatting

export function unmaskCPF(value: string): string {
  return onlyNumbers(value);
}

export function unmaskCNPJ(value: string): string {
  return onlyNumbers(value);
}

export function unmaskPhone(value: string): string {
  return onlyNumbers(value);
}

export function unmaskCEP(value: string): string {
  return onlyNumbers(value);
}

export function unmaskCNAE(value: string): string {
  return onlyNumbers(value);
}

export function unmaskCurrency(value: string): number {
  // Extrai apenas dígitos e trata como centavos (divide por 100).
  // Funciona para BRL ("R$ 1.234,56") e USD ("$1,234.56") porque
  // ignora separadores e interpreta os 2 últimos dígitos como centavos.
  const digits = value.replace(/\D/g, '');
  if (!digits) return 0;
  return parseInt(digits, 10) / 100;
}

export function unmaskNumber(value: string): number {
  const cleaned = value.replace(/[^\d,.-]/g, '').replace(/\.(?=.*[,.])/g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
}

// Convert date from DD/MM/YYYY to YYYY-MM-DD (ISO format)
export function dateToISO(value: string): string {
  const numbers = onlyNumbers(value);
  if (numbers.length < 8) return value; // Retorna valor parcial enquanto digita
  if (numbers.length !== 8) return '';

  const day = numbers.substring(0, 2);
  const month = numbers.substring(2, 4);
  const year = numbers.substring(4, 8);

  return `${year}-${month}-${day}`;
}

// Convert date from YYYY-MM-DD to DD/MM/YYYY
export function dateFromISO(value: string): string {
  if (!value) return '';

  // Se não é formato ISO (não tem -), retorna como está (valor parcial)
  if (!value.includes('-')) return value;

  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}
